import { createContext, useContext, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { Modal, Notification } from '../components'

const NotificationContext = createContext({
  success: () => undefined,
  error: () => undefined,
  alert: () => undefined
})

NotificationContext.displayName = 'NotificationContext'

export function NotificationContextProvider(props) {
  const { t } = useTranslation()

  const modalRef = useRef()
  const notificationRef = useRef()

  const safeAlertWithType = (...args) => {
    const alertWithType = notificationRef.current?.alertWithType
    if (typeof alertWithType !== 'function') return
    alertWithType(...args)
  }

  const safeModalAlert = (...args) => {
    const alert = modalRef.current?.alert
    if (typeof alert !== 'function') return
    alert(...args)
  }

  const value = useMemo(
    () => ({
      success: (message) => {
        safeAlertWithType('success', message)
      },
      error: (message) => {
        safeAlertWithType(
          'error',
          message || t('app.notification_error_default')
        )
      },
      alert: (title, messages, buttons, iconComponent, children, maxwmax) => {
        safeModalAlert(
          title,
          messages,
          buttons,
          iconComponent,
          children,
          maxwmax
        )
      }
    }),
    [t]
  )

  return (
    <>
      <NotificationContext.Provider value={value} {...props} />
      <Modal ref={modalRef} />
      <Notification ref={notificationRef} />
    </>
  )
}

export function useNotificationContext() {
  const context = useContext(NotificationContext)

  if (!context) {
    throw new Error('useNotificationContext must be used within a AppProvider')
  }

  return context
}
